<template>
	<div class="map">
		<yandex-map
			v-model="map"
			:settings="{
				location: {
					center: [0,0],
					zoom: 10,
				},
			}"
			width="100%"
			height="520px"
		>
			<yandex-map-default-scheme-layer/>
			<yandex-map-default-features-layer/>
			<yandex-map-marker
				v-for="point in points"
				:key="point.id"
				:marker-id="point.id"
				:settings="{ coordinates: point.coords, size: 'micro' }"
				class="map__marker"
				:class="active?.id === point.id ? 'map__marker--active' : ''"
				@click="setCurrentPoint(point)"
			></yandex-map-marker>
		</yandex-map>
		<map-current
			v-if="active"
			:point="active"
			@close="deleteCurrentPoint"
		></map-current>
	</div>
</template>

<script setup lang="ts">
import {watch, onMounted, ref, shallowRef} from 'vue';
import {
	YandexMap,
	YandexMapMarker,
	YandexMapDefaultSchemeLayer,
	YandexMapDefaultFeaturesLayer,
	getBoundsFromCoords,
	getLocationFromBounds,
} from 'vue-yandex-maps';
import type { YMap } from '@yandex/ymaps3-types';
import MapCurrent from './map-current/map-current.vue';

interface Point {
	id: string | number;
	name: string;
	address: string;
	metro?: string;
	coords: [number, number];
}

const props = defineProps<{
	href?: string;
	navigation?: boolean;
}>();

const points = ref<Point[] | null>(null);
const active = ref<Point | null>(null);

const setBounds = (newMap) => {
	if (!newMap.value) return;

	getLocationFromBounds({
		bounds: getBoundsFromCoords(points.value?.map((point) => point.coords)),
		map: newMap.value,
		roundZoom: true,
		comfortZoomLevel: true,
	}).then((result) => newMap.value.setLocation(result));
};

const getData = () => {
	fetch(props.href, {
		headers: {
			'Content-Type': 'application/json',
			'Accept': 'application/json',
		},
	})
		.then((response) => response.json())
		.then((response) => {
			points.value = response.data;
			setBounds(map);
		})
		.catch((error) => console.error(error));
};

const setCurrentPoint = (point) => {
	active.value = point;
};

const deleteCurrentPoint = () => (active.value = null);

const map = shallowRef<null | YMap>(null);

watch(map, (newMap) => setBounds({ value: newMap }));

onMounted(() => {
	getData();
	window.app.mapComponent = {
		setActive: (id: string): void => {
			const currentPoint = points.value.find((point) => point.id.toString() === id.toString());
			if (!currentPoint) return;
			map.value.setLocation({ center: currentPoint.coords, zoom: 12, duration: 300 });
		},
	};
});
</script>
